import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Stack,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import AutorenewRounded from "@mui/icons-material/AutorenewRounded";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FolderOpenRounded from "@mui/icons-material/FolderOpenRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import NavigateBeforeRounded from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRounded from "@mui/icons-material/NavigateNextRounded";
import FormatListBulletedRounded from "@mui/icons-material/FormatListBulletedRounded";
import TaskAltRounded from "@mui/icons-material/TaskAltRounded";
import ViewKanbanOutlined from "@mui/icons-material/ViewKanbanOutlined";
import WelcomeHeader from "../components/WelcomeHeader";
import taskAgentService from "../features/taskAgent/services/taskAgent.service";
import { getReadonlyFlag } from "../utils/getRoleToken";

const initialFolderFormState = {
  nomePasta: "",
  descricao: "",
  nomeWorkflow: "",
  ativo: true,
};

const initialTaskFormState = {
  titulo: "",
  descricao: "",
  idUsuarioResponsavel: "",
  dataInicio: "",
  dataPrazo: "",
  importancia: "NORMAL",
  idWorkflow: "",
};

const initialStageFormState = {
  nomeEtapa: "",
};

const initialStageEditFormState = {
  nomeEtapa: "",
  conclusivo: false,
  ordem: "1",
  ativo: true,
};

const initialCommentFormState = {
  comentario: "",
};

const importanceMap = {
  NORMAL: { color: "#4CAF50", label: "Normal" },
  IMPORTANTE: { color: "#FBC02D", label: "Importante" },
  URGENTE: { color: "#F44336", label: "Urgente" },
};

const getLocalDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isTaskOverdue = (task, todayDateString) => Boolean(task?.dataPrazo && task.dataPrazo < todayDateString && !task?.concluida);

const getTaskDueDate = (task) => task?.dataPrazo || "";
const getTaskCalendarDate = (task) => task?.dataPrazo || task?.dataInicio || "";

const getMonthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const getMonthEnd = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const getCalendarGridStart = (date) => {
  const start = getMonthStart(date);
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() - start.getDay());
};
const getCalendarGridEnd = (date) => {
  const end = getMonthEnd(date);
  return new Date(end.getFullYear(), end.getMonth(), end.getDate() + (6 - end.getDay()));
};
const formatMonthYearLabel = (date) => {
  const monthLabel = date.toLocaleDateString("pt-BR", { month: "long" });
  return `${monthLabel} ${date.getFullYear()}`;
};

const compareDueDates = (a, b, direction = "asc") => {
  const dueDateA = getTaskDueDate(a);
  const dueDateB = getTaskDueDate(b);
  const hasDueDateA = Boolean(dueDateA);
  const hasDueDateB = Boolean(dueDateB);

  if (!hasDueDateA && !hasDueDateB) return 0;
  if (!hasDueDateA) return 1;
  if (!hasDueDateB) return -1;

  return direction === "asc" ? dueDateA.localeCompare(dueDateB) : dueDateB.localeCompare(dueDateA);
};

const getApiErrorMessage = (err, fallbackMessage) => {
  const responseData = err?.response?.data;

  if (typeof responseData === "string" && responseData.trim()) return responseData;
  if (responseData?.message) return responseData.message;
  if (responseData?.erro) return responseData.erro;
  if (responseData?.detail) return responseData.detail;
  if (responseData?.details) return responseData.details;
  if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
    return responseData.errors.map((item) => item?.message || item).filter(Boolean).join(" | ");
  }

  return err?.message || fallbackMessage;
};

const sortFoldersByName = (folders = []) =>
  [...folders].sort((a, b) => (a?.nomePasta || "").localeCompare(b?.nomePasta || "", "pt-BR", { sensitivity: "base" }));

const buildFolderTree = (folders = []) => {
  const childrenByParent = folders.reduce((acc, folder) => {
    const parentKey = folder?.idPastaPai == null ? "root" : String(folder.idPastaPai);
    if (!acc[parentKey]) acc[parentKey] = [];
    acc[parentKey].push(folder);
    return acc;
  }, {});

  const mapChildren = (parentId = null) => {
    const key = parentId == null ? "root" : String(parentId);
    const children = sortFoldersByName(childrenByParent[key] || []);

    return children.map((folder) => ({
      id: folder.idPasta,
      label: folder.nomePasta || "Pasta sem nome",
      children: mapChildren(folder.idPasta),
    }));
  };

  return mapChildren();
};

const buildFolderPath = (folderId, folders = []) => {
  if (!folderId) return [];

  const foldersById = new Map(folders.map((folder) => [folder.idPasta, folder]));
  const path = [];
  let currentFolder = foldersById.get(folderId);

  while (currentFolder) {
    path.unshift(currentFolder);
    currentFolder = currentFolder.idPastaPai != null ? foldersById.get(currentFolder.idPastaPai) : null;
  }

  return path;
};

const TaskAgentPastas = () => {
  const [bootstrap, setBootstrap] = useState(null);
  const [pastas, setPastas] = useState([]);
  const [allPastas, setAllPastas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFolderDetails, setLoadingFolderDetails] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingWorkflow, setSavingWorkflow] = useState(false);
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [currentFolder, setCurrentFolder] = useState(null);
  const [currentWorkflow, setCurrentWorkflow] = useState(null);
  const [currentTasks, setCurrentTasks] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState(false);
  const [openEditFolderDialog, setOpenEditFolderDialog] = useState(false);
  const [openCreateTaskDialog, setOpenCreateTaskDialog] = useState(false);
  const [openCreateStageDialog, setOpenCreateStageDialog] = useState(false);
  const [openEditStageDialog, setOpenEditStageDialog] = useState(false);
  const [openDeleteFolderDialog, setOpenDeleteFolderDialog] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [stageToEdit, setStageToEdit] = useState(null);
  const [taskComments, setTaskComments] = useState([]);
  const [folderFormData, setFolderFormData] = useState(initialFolderFormState);
  const [taskFormData, setTaskFormData] = useState(initialTaskFormState);
  const [stageFormData, setStageFormData] = useState(initialStageFormState);
  const [stageEditFormData, setStageEditFormData] = useState(initialStageEditFormState);
  const [commentFormData, setCommentFormData] = useState(initialCommentFormState);
  const [taskViewMode, setTaskViewMode] = useState("board");
  const [calendarReferenceDate, setCalendarReferenceDate] = useState(() => getMonthStart(new Date()));
  const [taskSortOrder, setTaskSortOrder] = useState("date_asc");
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [folderStatusFilter, setFolderStatusFilter] = useState("active");
  const [draggingTaskId, setDraggingTaskId] = useState(null);
  const [dropStageId, setDropStageId] = useState(null);

  const boardScrollRef = useRef(null);
  const isBoardDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);

  const readonlyFlag = getReadonlyFlag();
  const isReadOnlyUser = readonlyFlag === true;

  const currentLevel = breadcrumb.length;
  const currentFolderHasTasks = Number(currentFolder?.totalTarefas || 0) > 0;
  const currentFolderHasSubpastas = Number(currentFolder?.totalSubpastas || 0) > 0;
  const canCreateRootFolder = !currentFolder;
  const canCreateSubfolder = Boolean(currentFolder) && !currentFolderHasTasks && currentLevel < 3;
  const canCreateFolder = !isReadOnlyUser && (canCreateRootFolder || canCreateSubfolder);
  const canCreateTask = !isReadOnlyUser && Boolean(currentFolder) && !currentFolderHasSubpastas;
  const showSubfolderButton = Boolean(currentFolder) && canCreateSubfolder;
  const isSubfolderContainerView = Boolean(currentFolder) && currentFolderHasSubpastas;
  const canDeleteSelectedFolder =
    Boolean(currentFolder) && !isReadOnlyUser && currentFolder?.tipoPasta !== "INTERNA";
  const canEditSelectedFolder =
    Boolean(currentFolder) && !isReadOnlyUser && currentFolder?.tipoPasta !== "INTERNA";

  const createRestrictionMessage = useMemo(() => {
    if (isReadOnlyUser) return "Seu perfil possui acesso somente leitura para o TaskAgent.";
    if (!currentFolder) return "";
    if (currentFolderHasTasks) return "Esta pasta possui tarefas. Nela, novas subpastas nao podem ser criadas.";
    if (currentLevel >= 3) return "A profundidade maxima permitida para a arvore de pastas e de 3 niveis.";
    return "";
  }, [currentFolder, currentFolderHasTasks, currentLevel, isReadOnlyUser]);

  const taskRestrictionMessage = useMemo(() => {
    if (isReadOnlyUser) return "Seu perfil possui acesso somente leitura para o TaskAgent.";
    if (!currentFolder) return "";
    if (currentFolderHasSubpastas) return "Esta pasta possui subpastas. Nela, novas tarefas nao podem ser criadas.";
    return "";
  }, [currentFolder, currentFolderHasSubpastas, isReadOnlyUser]);

  const workflowStages = useMemo(() => currentWorkflow?.etapas || [], [currentWorkflow]);
  const todayDateString = getLocalDateString();

  const stagesForBoard = useMemo(() => {
    return workflowStages
      .filter((etapa) => etapa.ativo !== false)
      .slice()
      .sort((a, b) => Number(a.ordem || 0) - Number(b.ordem || 0));
  }, [workflowStages]);

  const sortedTasks = useMemo(() => {
    const normalizedTasks = [...currentTasks];

    normalizedTasks.sort((a, b) => {
      switch (taskSortOrder) {
        case "date_asc":
          return compareDueDates(a, b, "asc");
        case "date_desc":
          return compareDueDates(a, b, "desc");
        case "name_asc":
          return (a?.titulo || "").localeCompare(b?.titulo || "", "pt-BR", { sensitivity: "base" });
        case "name_desc":
          return (b?.titulo || "").localeCompare(a?.titulo || "", "pt-BR", { sensitivity: "base" });
        case "responsavel_asc":
          return (a?.nomeResponsavel || "").localeCompare(b?.nomeResponsavel || "", "pt-BR", { sensitivity: "base" });
        case "responsavel_desc":
          return (b?.nomeResponsavel || "").localeCompare(a?.nomeResponsavel || "", "pt-BR", { sensitivity: "base" });
        default:
          return 0;
      }
    });

    return normalizedTasks;
  }, [currentTasks, taskSortOrder]);

  const visibleTasks = useMemo(
    () => (showCompletedTasks ? sortedTasks : sortedTasks.filter((tarefa) => !tarefa?.concluida)),
    [showCompletedTasks, sortedTasks]
  );

  const tasksByWorkflow = useMemo(() => {
    return visibleTasks.reduce((acc, tarefa) => {
      const key = String(tarefa.idWorkflow || "sem-workflow");
      if (!acc[key]) acc[key] = [];
      acc[key].push(tarefa);
      return acc;
    }, {});
  }, [visibleTasks]);

  const calendarTasksByDate = useMemo(() => {
    return visibleTasks.reduce((acc, tarefa) => {
      const dateKey = getTaskCalendarDate(tarefa);
      if (!dateKey) return acc;
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(tarefa);
      return acc;
    }, {});
  }, [visibleTasks]);

  const calendarDays = useMemo(() => {
    const start = getCalendarGridStart(calendarReferenceDate);
    const end = getCalendarGridEnd(calendarReferenceDate);
    const days = [];
    const current = new Date(start);

    while (current <= end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [calendarReferenceDate]);

  const calendarMonthLabel = useMemo(() => formatMonthYearLabel(calendarReferenceDate), [calendarReferenceDate]);

  const loadBootstrap = useCallback(async () => {
    const data = await taskAgentService.getBootstrap();
    setBootstrap(data);
  }, []);

  const loadPastas = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const visibleFilters = {};
      const treeFilters = {};

      if (currentFolder?.idPasta) visibleFilters.idPastaPai = currentFolder.idPasta;
      if (folderStatusFilter === "active") {
        visibleFilters.ativo = true;
        treeFilters.ativo = true;
      }
      if (folderStatusFilter === "inactive") {
        visibleFilters.ativo = false;
        treeFilters.ativo = false;
      }

      const [visibleData, allData] = await Promise.all([
        taskAgentService.listPastas(visibleFilters),
        taskAgentService.listPastas(treeFilters),
      ]);

      const normalizedVisiblePastas = Array.isArray(visibleData) ? visibleData : [];
      const normalizedAllPastas = Array.isArray(allData) ? allData : [];
      const visiblePastas = currentFolder
        ? normalizedVisiblePastas
        : normalizedAllPastas.filter((pasta) => pasta?.idPastaPai == null);

      setAllPastas(normalizedAllPastas);
      setPastas(visiblePastas);
    } catch (err) {
      setError(getApiErrorMessage(err, "Erro ao carregar pastas."));
    } finally {
      setLoading(false);
    }
  }, [currentFolder, folderStatusFilter]);

  const loadSelectedFolderDetails = useCallback(async (folder) => {
    if (!folder?.idPasta) {
      setCurrentWorkflow(null);
      setCurrentTasks([]);
      setResponsaveis([]);
      return;
    }

    try {
      setLoadingFolderDetails(true);
      const [workflowData, tasksData, responsaveisData] = await Promise.all([
        taskAgentService.getWorkflow(folder.idPasta).catch(() => null),
        taskAgentService.listTarefas(folder.idPasta).catch(() => []),
        taskAgentService.listResponsaveis({ ativo: true }).catch(() => []),
      ]);

      setCurrentWorkflow(workflowData);
      setCurrentTasks(Array.isArray(tasksData) ? tasksData : []);
      setResponsaveis(Array.isArray(responsaveisData) ? responsaveisData : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Erro ao carregar os detalhes da pasta."));
    } finally {
      setLoadingFolderDetails(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    const loadPage = async () => {
      try {
        setLoading(true);
        setError("");
        const [bootstrapData, pastasData] = await Promise.all([
          taskAgentService.getBootstrap(),
          taskAgentService.listPastas(),
        ]);

        if (!active) return;
        setBootstrap(bootstrapData);
        const normalizedPastas = Array.isArray(pastasData) ? pastasData : [];
        setAllPastas(normalizedPastas);
        setPastas(normalizedPastas.filter((pasta) => pasta?.idPastaPai == null));
      } catch (err) {
        if (active) setError(getApiErrorMessage(err, "Erro ao carregar dados iniciais das pastas."));
      } finally {
        if (active) setLoading(false);
      }
    };

    loadPage();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!bootstrap) return;
    loadPastas();
  }, [bootstrap, loadPastas]);

  useEffect(() => {
    loadSelectedFolderDetails(currentFolder);
  }, [currentFolder, loadSelectedFolderDetails]);

  useEffect(() => {
    setTaskFormData((current) => ({
      ...current,
      idWorkflow: stagesForBoard[0]?.idWorkflow ? String(stagesForBoard[0].idWorkflow) : "",
    }));
  }, [currentFolder, stagesForBoard]);

  useEffect(() => {
    if (!successMessage) return undefined;

    const timer = window.setTimeout(() => {
      setSuccessMessage("");
    }, 2500);

    return () => window.clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    let active = true;

    const loadTaskComments = async () => {
      if (!openCreateTaskDialog || !taskToEdit?.idTarefa || !currentFolder?.idPasta) {
        if (active) setTaskComments([]);
        return;
      }

      try {
        setLoadingComments(true);
        const commentsData = await taskAgentService.listComentarios(currentFolder.idPasta, taskToEdit.idTarefa);
        if (!active) return;
        setTaskComments(Array.isArray(commentsData) ? commentsData : []);
      } catch (err) {
        if (active) {
          setTaskComments([]);
          setModalError(getApiErrorMessage(err, "Erro ao carregar comentarios da tarefa."));
        }
      } finally {
        if (active) setLoadingComments(false);
      }
    };

    loadTaskComments();

    return () => {
      active = false;
    };
  }, [openCreateTaskDialog, taskToEdit, currentFolder]);

  useEffect(() => {
    const openRootPastas = () => {
      setCurrentFolder(null);
      setCurrentWorkflow(null);
      setCurrentTasks([]);
      setResponsaveis([]);
      setBreadcrumb([]);
      setStageFormData(initialStageFormState);
      setTaskViewMode("board");
      setFolderStatusFilter("active");
      setSuccessMessage("");
      setError("");
    };

    window.addEventListener("taskagent:open-root-pastas", openRootPastas);
    return () => window.removeEventListener("taskagent:open-root-pastas", openRootPastas);
  }, []);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("taskagent:folder-context", {
        detail: {
          currentFolderId: currentFolder?.idPasta ?? null,
          folderPath: breadcrumb.map((item) => item?.nomePasta).filter(Boolean),
          folderPathIds: breadcrumb.map((item) => item?.idPasta).filter(Boolean),
          folderTree: buildFolderTree(allPastas),
        },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent("taskagent:folder-context", {
          detail: { currentFolderId: null, folderPath: [], folderPathIds: [], folderTree: [] },
        })
      );
    };
  }, [allPastas, breadcrumb, currentFolder]);

  useEffect(() => {
    const handleSidebarFolderNavigate = (event) => {
      const targetFolderId = Number(event.detail?.folderId);

      if (!targetFolderId) {
        handleGoToRoot();
        return;
      }

      const targetFolder = allPastas.find((folder) => Number(folder?.idPasta) === targetFolderId);
      if (!targetFolder) return;

      const targetPath = buildFolderPath(targetFolderId, allPastas);

      setBreadcrumb(targetPath);
      setCurrentFolder(targetFolder);
      setCurrentWorkflow(null);
      setCurrentTasks([]);
      setStageFormData(initialStageFormState);
      setTaskViewMode("board");
      setSuccessMessage("");
      setError("");
    };

    window.addEventListener("taskagent:navigate-folder", handleSidebarFolderNavigate);
    return () => window.removeEventListener("taskagent:navigate-folder", handleSidebarFolderNavigate);
  }, [allPastas]);

  const handleOpenFolder = (folder) => {
    setBreadcrumb((current) => [...current, folder]);
    setCurrentFolder(folder);
    setCurrentWorkflow(null);
    setCurrentTasks([]);
    setStageFormData(initialStageFormState);
      setTaskViewMode("board");
    setSuccessMessage("");
  };

  const handleGoToRoot = () => {
    setCurrentFolder(null);
    setCurrentWorkflow(null);
    setCurrentTasks([]);
    setResponsaveis([]);
    setBreadcrumb([]);
    setStageFormData(initialStageFormState);
    setTaskViewMode("board");
    setFolderStatusFilter("active");
    setSuccessMessage("");
  };

  const handleBreadcrumbNavigate = (index) => {
    if (index < 0) {
      handleGoToRoot();
      return;
    }

    const targetFolder = breadcrumb[index];
    if (!targetFolder) return;

    setBreadcrumb((current) => current.slice(0, index + 1));
    setCurrentFolder(targetFolder);
    setCurrentWorkflow(null);
    setCurrentTasks([]);
    setStageFormData(initialStageFormState);
    setTaskViewMode("board");
    setSuccessMessage("");
    setError("");
  };

  const handleGoToPreviousFolder = () => {
    if (breadcrumb.length <= 1) {
      handleGoToRoot();
      return;
    }

    handleBreadcrumbNavigate(breadcrumb.length - 2);
  };

  const handleFolderFormChange = (event) => {
    const { name, value } = event.target;
    setFolderFormData((current) => ({ ...current, [name]: value }));
  };

  const handleFolderActiveChange = (event) => {
    setFolderFormData((current) => ({
      ...current,
      ativo: event.target.checked,
    }));
  };

  const handleTaskFormChange = (event) => {
    const { name, value } = event.target;
    setTaskFormData((current) => ({ ...current, [name]: value }));
  };

  const handleCommentFormChange = (event) => {
    const { name, value } = event.target;
    setCommentFormData((current) => ({ ...current, [name]: value }));
  };

  const handleStageFormChange = (event) => {
    const { name, value } = event.target;
    setStageFormData((current) => ({ ...current, [name]: value }));
  };

  const handleStageEditFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setStageEditFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCloseFolderDialog = () => {
    setOpenCreateFolderDialog(false);
    setFolderFormData(initialFolderFormState);
    setModalError("");
  };

  const handleOpenEditFolderDialog = (folder) => {
    if (!folder || isReadOnlyUser || folder.tipoPasta === "INTERNA") return;

    setFolderToEdit(folder);
    setFolderFormData({
      nomePasta: folder.nomePasta || "",
      descricao: folder.descricao || "",
      nomeWorkflow: folder.nomeWorkflow || "",
      ativo: folder.ativo ?? true,
    });
    setModalError("");
    setOpenEditFolderDialog(true);
  };

  const handleCloseEditFolderDialog = () => {
    setOpenEditFolderDialog(false);
    setFolderToEdit(null);
    setFolderFormData(initialFolderFormState);
    setModalError("");
  };

  const handleCloseTaskDialog = () => {
    setOpenCreateTaskDialog(false);
    setTaskToEdit(null);
    setTaskFormData(initialTaskFormState);
    setTaskComments([]);
    setCommentFormData(initialCommentFormState);
    setModalError("");
  };

  const handleOpenCreateTaskDialog = (workflowId = null) => {
    if (!canCreateTask) return;

    setTaskToEdit(null);
    setTaskFormData({
      ...initialTaskFormState,
      importancia: "NORMAL",
      idWorkflow: workflowId ? String(workflowId) : stagesForBoard[0]?.idWorkflow ? String(stagesForBoard[0].idWorkflow) : "",
    });
    setModalError("");
    setOpenCreateTaskDialog(true);
  };

  const handleOpenEditTaskDialog = (tarefa) => {
    if (!tarefa?.idTarefa) return;

    setTaskToEdit(tarefa);
    setTaskFormData({
      titulo: tarefa.titulo || "",
      descricao: tarefa.descricao || "",
      idUsuarioResponsavel: tarefa.idUsuarioResponsavel ? String(tarefa.idUsuarioResponsavel) : "",
      dataInicio: tarefa.dataInicio || "",
      dataPrazo: tarefa.dataPrazo || "",
      importancia: tarefa.importancia || "NORMAL",
      idWorkflow: tarefa.idWorkflow ? String(tarefa.idWorkflow) : "",
    });
    setModalError("");
    setOpenCreateTaskDialog(true);
  };

  const handleOpenCreateStageDialog = () => {
    if (isReadOnlyUser || !currentFolder?.idPasta) return;
    setStageFormData(initialStageFormState);
    setModalError("");
    setOpenCreateStageDialog(true);
  };

  const handleCloseCreateStageDialog = () => {
    setOpenCreateStageDialog(false);
    setStageFormData(initialStageFormState);
    setModalError("");
  };

  const handleOpenEditStageDialog = (etapa) => {
    if (!etapa?.idWorkflow || isReadOnlyUser) return;

    setStageToEdit(etapa);
    setStageEditFormData({
      nomeEtapa: etapa.nomeEtapa || "",
      conclusivo: Boolean(etapa.conclusivo),
      ordem: String(etapa.ordem || 1),
      ativo: etapa.ativo !== false,
    });
    setModalError("");
    setOpenEditStageDialog(true);
  };

  const handleCloseEditStageDialog = () => {
    setOpenEditStageDialog(false);
    setStageToEdit(null);
    setStageEditFormData(initialStageEditFormState);
    setModalError("");
  };

  const handleOpenDeleteFolderDialog = (folder) => {
    if (!folder || isReadOnlyUser || folder.tipoPasta === "INTERNA") return;
    setFolderToDelete(folder);
    setModalError("");
    setOpenDeleteFolderDialog(true);
  };

  const handleCloseDeleteFolderDialog = () => {
    setOpenDeleteFolderDialog(false);
    setFolderToDelete(null);
    setModalError("");
  };

  const handleCreateFolder = async () => {
    if (isReadOnlyUser) {
      setModalError("Seu usuario possui apenas permissao de leitura para o TaskAgent.");
      return;
    }

    if (!folderFormData.nomePasta.trim()) {
      setModalError("Informe o nome da pasta para continuar.");
      return;
    }

    try {
      setSubmitting(true);
      setModalError("");
      setSuccessMessage("");
      await taskAgentService.createPasta({
        idPastaPai: currentFolder?.idPasta ?? null,
        nomePasta: folderFormData.nomePasta.trim(),
        descricao: folderFormData.descricao.trim(),
        nomeWorkflow: folderFormData.nomeWorkflow.trim(),
        ativo: true,
      });

      setSuccessMessage("Pasta criada com sucesso.");
      handleCloseFolderDialog();
      await Promise.all([loadBootstrap(), loadPastas()]);

      if (currentFolder) {
        const refreshedFolder = await taskAgentService.getPasta(currentFolder.idPasta);
        setCurrentFolder(refreshedFolder);
      }
    } catch (err) {
      setModalError(getApiErrorMessage(err, "Erro ao criar pasta."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateFolder = async () => {
    if (!folderToEdit?.idPasta) return;

    if (isReadOnlyUser) {
      setModalError("Seu usuario possui apenas permissao de leitura para o TaskAgent.");
      return;
    }

    if (!folderFormData.nomePasta.trim()) {
      setModalError("Informe o nome da pasta para continuar.");
      return;
    }

    try {
      setSubmitting(true);
      setModalError("");
      setSuccessMessage("");

      await taskAgentService.updatePasta(folderToEdit.idPasta, {
        idPastaPai: folderToEdit.idPastaPai ?? null,
        nomePasta: folderFormData.nomePasta.trim(),
        descricao: folderFormData.descricao.trim(),
        nomeWorkflow: folderFormData.nomeWorkflow.trim(),
        ativo: folderFormData.ativo ?? true,
      });

      handleCloseEditFolderDialog();
      setSuccessMessage("Pasta atualizada com sucesso.");

      await loadPastas();

      if (currentFolder?.idPasta === folderToEdit.idPasta) {
        const refreshedFolder = await taskAgentService.getPasta(folderToEdit.idPasta);
        setCurrentFolder(refreshedFolder);
      }
    } catch (err) {
      setModalError(getApiErrorMessage(err, "Erro ao atualizar pasta."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitTask = async () => {
    if (!currentFolder?.idPasta) {
      setModalError("Selecione uma pasta para criar a tarefa.");
      return;
    }

    if (isReadOnlyUser) {
      setModalError("Seu usuario possui apenas permissao de leitura para o TaskAgent.");
      return;
    }

    if (!taskFormData.titulo.trim()) {
      setModalError("Informe o titulo da tarefa.");
      return;
    }

    if (!taskFormData.idWorkflow) {
      setModalError("Crie ao menos uma etapa de workflow antes de cadastrar tarefas nesta pasta.");
      return;
    }

    try {
      setSubmitting(true);
      setModalError("");
      setSuccessMessage("");

      const payload = {
        titulo: taskFormData.titulo.trim(),
        descricao: taskFormData.descricao.trim(),
        idUsuarioResponsavel: taskFormData.idUsuarioResponsavel ? Number(taskFormData.idUsuarioResponsavel) : null,
        dataInicio: taskFormData.dataInicio || null,
        dataPrazo: taskFormData.dataPrazo || null,
        dataConclusao: null,
        importancia: taskFormData.importancia || "NORMAL",
        idWorkflow: Number(taskFormData.idWorkflow),
      };

      if (taskToEdit?.idTarefa) {
        await taskAgentService.updateTarefa(currentFolder.idPasta, taskToEdit.idTarefa, payload);
      } else {
        await taskAgentService.createTarefa(currentFolder.idPasta, payload);
      }

      setSuccessMessage(taskToEdit?.idTarefa ? "Tarefa atualizada com sucesso." : "Tarefa criada com sucesso.");
      handleCloseTaskDialog();

      const [tasksData, refreshedFolder] = await Promise.all([
        taskAgentService.listTarefas(currentFolder.idPasta),
        taskAgentService.getPasta(currentFolder.idPasta),
      ]);

      setCurrentTasks(Array.isArray(tasksData) ? tasksData : []);
      setCurrentFolder(refreshedFolder);
      await loadPastas();
    } catch (err) {
      setModalError(getApiErrorMessage(err, taskToEdit?.idTarefa ? "Erro ao atualizar tarefa." : "Erro ao criar tarefa."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddWorkflowStage = async () => {
    if (!currentFolder?.idPasta) return;

    if (isReadOnlyUser) {
      setModalError("Seu usuario possui apenas permissao de leitura para o TaskAgent.");
      return;
    }

    const normalizedStageName = stageFormData.nomeEtapa.trim();
    if (!normalizedStageName) return;

    const existingStages = (currentWorkflow?.etapas || []).filter((etapa) => etapa?.ativo !== false);
    const hasDuplicateName = existingStages.some(
      (etapa) => etapa.nomeEtapa.trim().toLowerCase() === normalizedStageName.toLowerCase()
    );

    if (hasDuplicateName) {
      setModalError("Ja existe uma etapa com esse nome nesta pasta.");
      return;
    }

    try {
      setSavingWorkflow(true);
      setModalError("");
      const workflowData = await taskAgentService.createWorkflowStage(currentFolder.idPasta, {
        nomeEtapa: normalizedStageName,
        conclusivo: false,
      });

      setSuccessMessage("Etapa adicionada com sucesso.");
      handleCloseCreateStageDialog();
      setCurrentWorkflow(workflowData);
    } catch (err) {
      setModalError(getApiErrorMessage(err, "Erro ao adicionar nova etapa."));
    } finally {
      setSavingWorkflow(false);
    }
  };

  const handleCreateComment = async () => {
    if (!currentFolder?.idPasta || !taskToEdit?.idTarefa) return;

    if (isReadOnlyUser) {
      setModalError("Seu usuario possui apenas permissao de leitura para o TaskAgent.");
      return;
    }

    if (!commentFormData.comentario.trim()) {
      setModalError("Informe um comentario para continuar.");
      return;
    }

    try {
      setSubmitting(true);
      setModalError("");
      await taskAgentService.addComentario(currentFolder.idPasta, taskToEdit.idTarefa, commentFormData.comentario.trim());
      const commentsData = await taskAgentService.listComentarios(currentFolder.idPasta, taskToEdit.idTarefa);
      setTaskComments(Array.isArray(commentsData) ? commentsData : []);
      setCommentFormData(initialCommentFormState);
      setSuccessMessage("Comentario adicionado com sucesso.");
    } catch (err) {
      setModalError(getApiErrorMessage(err, "Erro ao adicionar comentario."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStage = async () => {
    if (!currentFolder?.idPasta || !stageToEdit?.idWorkflow) return;

    if (isReadOnlyUser) {
      setModalError("Seu usuario possui apenas permissao de leitura para o TaskAgent.");
      return;
    }

    if (!stageEditFormData.nomeEtapa.trim()) {
      setModalError("Informe o nome da etapa.");
      return;
    }

    try {
      setSavingWorkflow(true);
      setModalError("");
      const workflowData = await taskAgentService.updateWorkflowStage(currentFolder.idPasta, {
        idWorkflow: stageToEdit.idWorkflow,
        nomeEtapa: stageEditFormData.nomeEtapa.trim(),
        conclusivo: Boolean(stageEditFormData.conclusivo),
        ativo: stageEditFormData.ativo !== false,
        ordem: Number(stageEditFormData.ordem),
      });

      setCurrentWorkflow(workflowData);
      setSuccessMessage("Etapa atualizada com sucesso.");
      handleCloseEditStageDialog();
    } catch (err) {
      setModalError(getApiErrorMessage(err, "Erro ao atualizar etapa."));
    } finally {
      setSavingWorkflow(false);
    }
  };

  const handleInactivateStage = async () => {
    if (!currentFolder?.idPasta || !stageToEdit?.idWorkflow) return;

    try {
      setSavingWorkflow(true);
      setModalError("");
      const workflowData = await taskAgentService.inactivateWorkflowStage(currentFolder.idPasta, stageToEdit.idWorkflow);
      setCurrentWorkflow(workflowData);
      setSuccessMessage("Etapa inativada com sucesso.");
      handleCloseEditStageDialog();
    } catch (err) {
      setModalError(getApiErrorMessage(err, "Erro ao inativar etapa."));
    } finally {
      setSavingWorkflow(false);
    }
  };

  const handleBoardMouseDown = (event) => {
    if (event.button !== 0 || !boardScrollRef.current) return;

    isBoardDraggingRef.current = true;
    dragStartXRef.current = event.clientX;
    dragStartScrollLeftRef.current = boardScrollRef.current.scrollLeft;
    boardScrollRef.current.style.cursor = "grabbing";
    boardScrollRef.current.style.userSelect = "none";
  };

  const handleBoardMouseMove = (event) => {
    if (!isBoardDraggingRef.current || !boardScrollRef.current) return;

    const deltaX = event.clientX - dragStartXRef.current;
    boardScrollRef.current.scrollLeft = dragStartScrollLeftRef.current - deltaX;
  };

  const handleBoardMouseUp = () => {
    if (!boardScrollRef.current) return;
    isBoardDraggingRef.current = false;
    boardScrollRef.current.style.cursor = "grab";
    boardScrollRef.current.style.removeProperty("user-select");
  };

  const handleTaskDragStart = (event, tarefa) => {
    isBoardDraggingRef.current = false;
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(tarefa.idTarefa));
    setDraggingTaskId(tarefa.idTarefa);
  };

  const handleTaskDragEnd = () => {
    setDraggingTaskId(null);
    setDropStageId(null);
  };

  const handleStageDragOver = (event, etapa) => {
    if (!draggingTaskId || !etapa?.idWorkflow) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDropStageId(etapa.idWorkflow);
  };

  const handleStageDragLeave = (etapa) => {
    if (dropStageId === etapa?.idWorkflow) {
      setDropStageId(null);
    }
  };

  const handleStageDrop = async (event, etapa) => {
    event.preventDefault();
    setDropStageId(null);

    const tarefaId = Number(event.dataTransfer.getData("text/plain") || draggingTaskId);
    if (!tarefaId || !etapa?.idWorkflow || !currentFolder?.idPasta) return;

    const tarefaAtual = currentTasks.find((item) => item.idTarefa === tarefaId);
    if (!tarefaAtual || Number(tarefaAtual.idWorkflow) === Number(etapa.idWorkflow)) return;

    const previousTasks = currentTasks;
    const optimisticTasks = currentTasks.map((item) =>
      item.idTarefa === tarefaId
        ? {
            ...item,
            idWorkflow: Number(etapa.idWorkflow),
            nomeEtapa: etapa.nomeEtapa,
            concluida: Boolean(etapa.conclusivo),
            dataConclusao: etapa.conclusivo ? todayDateString : null,
          }
        : item
    );

    try {
      setSubmitting(true);
      setError("");
      setCurrentTasks(optimisticTasks);
      try {
        await taskAgentService.moveTarefaWorkflow(currentFolder.idPasta, tarefaId, Number(etapa.idWorkflow));
      } catch (moveError) {
        await taskAgentService.updateTarefa(currentFolder.idPasta, tarefaId, {
          titulo: tarefaAtual.titulo || "",
          descricao: tarefaAtual.descricao || "",
          idUsuarioResponsavel: tarefaAtual.idUsuarioResponsavel ?? null,
          dataInicio: tarefaAtual.dataInicio || null,
          dataPrazo: tarefaAtual.dataPrazo || null,
          dataConclusao: tarefaAtual.dataConclusao || null,
          importancia: tarefaAtual.importancia || "NORMAL",
          idWorkflow: Number(etapa.idWorkflow),
        });
      }

      const tasksData = await taskAgentService.listTarefas(currentFolder.idPasta);
      setCurrentTasks(Array.isArray(tasksData) ? tasksData : []);
      setSuccessMessage("Tarefa movida com sucesso.");
    } catch (err) {
      setCurrentTasks(previousTasks);
      setError(getApiErrorMessage(err, "Erro ao mover tarefa."));
    } finally {
      setSubmitting(false);
      setDraggingTaskId(null);
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete?.idPasta) return;

    try {
      setSubmitting(true);
      setModalError("");
      setSuccessMessage("");

      await taskAgentService.deletePasta(folderToDelete.idPasta);

      const isDeletingSelectedFolder = currentFolder?.idPasta === folderToDelete.idPasta;

      handleCloseDeleteFolderDialog();
      setSuccessMessage("Pasta excluida com sucesso.");

      if (isDeletingSelectedFolder) {
        setCurrentFolder(null);
        setCurrentWorkflow(null);
        setCurrentTasks([]);
        setResponsaveis([]);
        setBreadcrumb([]);
    setTaskViewMode("board");
      }

      await Promise.all([loadBootstrap(), loadPastas()]);
    } catch (err) {
      setModalError(getApiErrorMessage(err, "Erro ao excluir pasta."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivateFolder = async (folder) => {
    if (!folder?.idPasta) return;

    try {
      setSubmitting(true);
      setError("");
      setSuccessMessage("");

      await taskAgentService.updatePasta(folder.idPasta, {
        idPastaPai: folder.idPastaPai ?? null,
        nomePasta: folder.nomePasta,
        descricao: folder.descricao || "",
        nomeWorkflow: folder.nomeWorkflow || "",
        ativo: true,
      });

      setSuccessMessage(`Pasta "${folder.nomePasta}" reativada com sucesso.`);
      await Promise.all([loadBootstrap(), loadPastas()]);
    } catch (err) {
      setError(getApiErrorMessage(err, "Erro ao reativar pasta."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <WelcomeHeader title="TaskAgent" subtitle="" />

      {breadcrumb.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: 3,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.72)" : "rgba(248, 250, 252, 0.96)"),
          }}
        >
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" useFlexGap flexWrap="wrap">
            <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
              <Box
                component="button"
                type="button"
                onClick={() => handleBreadcrumbNavigate(-1)}
                sx={{
                  border: "none",
                  bgcolor: "transparent",
                  color: "text.secondary",
                  cursor: "pointer",
                  p: 0,
                  fontSize: "0.95rem",
                  "&:hover": { color: "primary.main" },
                }}
              >
                Processos
              </Box>

              {breadcrumb.map((item, index) => {
                const isLast = index === breadcrumb.length - 1;

                return (
                  <Stack key={item.idPasta} direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <ChevronRightRounded sx={{ fontSize: 18, color: "text.secondary" }} />
                    {isLast ? (
                      <Typography variant="body2" noWrap sx={{ color: "text.primary", fontWeight: 700 }}>
                        {item.nomePasta}
                      </Typography>
                    ) : (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => handleBreadcrumbNavigate(index)}
                        sx={{
                          border: "none",
                          bgcolor: "transparent",
                          color: "text.secondary",
                          cursor: "pointer",
                          p: 0,
                          fontSize: "0.95rem",
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        {item.nomePasta}
                      </Box>
                    )}
                  </Stack>
                );
              })}
            </Stack>

            {currentFolder && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                {!!currentFolder.descricao && (
                  <Tooltip title={currentFolder.descricao}>
                    <InfoOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
                  </Tooltip>
                )}
                {currentFolder.tipoPasta === "INTERNA" && <Chip label="Pasta interna" size="small" color="warning" />}

                {showSubfolderButton && (
                  <Tooltip title="Adicionar sub-pasta">
                    <span>
                      <IconButton size="small" onClick={() => setOpenCreateFolderDialog(true)}>
                        <AddRounded fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                {canEditSelectedFolder && (
                  <Tooltip title="Editar pasta">
                    <span>
                      <IconButton size="small" onClick={() => handleOpenEditFolderDialog(currentFolder)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                {canDeleteSelectedFolder && (
                  <Tooltip title="Excluir pasta">
                    <span>
                      <IconButton size="small" color="error" onClick={() => handleOpenDeleteFolderDialog(currentFolder)}>
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                <Tooltip title="Voltar">
                  <IconButton onClick={handleGoToPreviousFolder} size="small" aria-label="Voltar para pasta anterior">
                    <ArrowBackRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      {!currentFolder && (
        <Stack spacing={1}>
          {createRestrictionMessage && (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              {createRestrictionMessage}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ borderRadius: 3 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ borderRadius: 3 }}>
              {successMessage}
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.25 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={folderStatusFilter}
                onChange={(_, value) => {
                  if (value) setFolderStatusFilter(value);
                }}
              >
                <ToggleButton value="active">Ativos</ToggleButton>
                <ToggleButton value="all">Todos</ToggleButton>
                <ToggleButton value="inactive">Inativos</ToggleButton>
              </ToggleButtonGroup>

              <Button
                variant="contained"
                startIcon={<AddRounded />}
                onClick={() => setOpenCreateFolderDialog(true)}
                disabled={!canCreateFolder}
              >
                Nova pasta
              </Button>
            </Stack>
          </Box>
        </Stack>
      )}

      {currentFolder && (
        <Stack spacing={0.75}>
          {error && (
            <Alert severity="error" sx={{ borderRadius: 3 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ borderRadius: 3 }}>
              {successMessage}
            </Alert>
          )}
        </Stack>
      )}

      {currentFolder ? (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: (theme) => `1px solid ${theme.palette.divider}` }}>
          {loadingFolderDetails ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : isSubfolderContainerView ? (
            <Stack spacing={1}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FolderOpenRounded color="primary" fontSize="small" />
                  <Chip label={`${pastas.length} subpastas`} size="small" variant="outlined" />
                </Stack>

                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={folderStatusFilter}
                  onChange={(_, value) => {
                    if (value) setFolderStatusFilter(value);
                  }}
                >
                  <ToggleButton value="active">Ativos</ToggleButton>
                  <ToggleButton value="all">Todos</ToggleButton>
                  <ToggleButton value="inactive">Inativos</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              {pastas.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: "1px dashed rgba(25, 118, 210, 0.22)",
                    bgcolor: "#fff",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma subpasta encontrada nesta pasta.
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      display: { xs: "none", md: "grid" },
                      gridTemplateColumns: "minmax(280px, 1.7fr) minmax(160px, 1fr) minmax(160px, 0.8fr) 180px",
                      gap: 2,
                      px: 2,
                      pb: 1,
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="caption" fontWeight={700}>
                      Subpasta
                    </Typography>
                    <Typography variant="caption" fontWeight={700}>
                      Workflow
                    </Typography>
                    <Typography variant="caption" fontWeight={700}>
                      Estrutura
                    </Typography>
                    <Typography variant="caption" fontWeight={700}>
                      Acao
                    </Typography>
                  </Box>

                  {pastas.map((pasta) => (
                    <Paper
                      key={pasta.idPasta}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border:
                          pasta.ativo === false
                            ? "1px solid rgba(244, 67, 54, 0.28)"
                            : (theme) => `1px solid ${theme.palette.divider}`,
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "minmax(280px, 1.7fr) minmax(160px, 1fr) minmax(160px, 0.8fr) 180px",
                        },
                        gap: 2,
                        alignItems: "center",
                        background:
                          pasta.ativo === false
                            ? "linear-gradient(135deg, rgba(244, 67, 54, 0.10) 0%, rgba(244, 67, 54, 0.04) 100%)"
                            : "transparent",
                        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 12px 28px rgba(23, 33, 77, 0.08)",
                          borderColor: pasta.ativo === false ? "rgba(244, 67, 54, 0.45)" : "rgba(25,118,210,0.28)",
                        },
                      }}
                      onClick={() => handleOpenFolder(pasta)}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 3,
                            display: "grid",
                            placeItems: "center",
                            bgcolor: pasta.ativo === false ? "rgba(244, 67, 54, 0.14)" : "rgba(25,118,210,0.08)",
                            color: pasta.ativo === false ? "error.main" : "primary.main",
                            flexShrink: 0,
                          }}
                        >
                          <FolderOpenRounded fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                              {pasta.nomePasta}
                            </Typography>
                            {pasta.ativo === false && <Chip label="Inativa" size="small" color="default" />}
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {pasta.descricao || "Sem descricao cadastrada."}
                          </Typography>
                        </Box>
                      </Stack>

                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {pasta.nomeWorkflow || "Workflow sem nome"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Fluxo da pasta
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip label={`${pasta.totalSubpastas || 0} subpastas`} size="small" variant="outlined" />
                        <Chip label={`${pasta.totalTarefas || 0} tarefas`} size="small" variant="outlined" />
                      </Stack>

                      <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {pasta.ativo === false && !isReadOnlyUser && (
                            <Tooltip title="Reativar pasta">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleReactivateFolder(pasta);
                                  }}
                                >
                                  <AutorenewRounded fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {pasta.tipoPasta !== "INTERNA" && !isReadOnlyUser && (
                            <Tooltip title="Editar pasta">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenEditFolderDialog(pasta);
                                  }}
                                >
                                  <EditOutlined fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {pasta.tipoPasta !== "INTERNA" && !isReadOnlyUser && (
                            <Tooltip title="Excluir pasta">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenDeleteFolderDialog(pasta);
                                  }}
                                >
                                  <DeleteOutlineRounded fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          <Button
                            variant="text"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenFolder(pasta);
                            }}
                            startIcon={<TaskAltRounded />}
                          >
                            Abrir pasta
                          </Button>
                        </Stack>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={taskViewMode}
                  onChange={(_, value) => {
                    if (value) setTaskViewMode(value);
                  }}
                  sx={{
                    "& .MuiToggleButton-root": {
                      px: 1.25,
                      py: 0.5,
                      minWidth: "auto",
                      fontSize: 13,
                      textTransform: "none",
                      borderColor: "divider",
                    },
                  }}
                >
                  <ToggleButton value="board">
                    <ViewKanbanOutlined sx={{ mr: 0.5, fontSize: 16 }} />
                    Quadro
                  </ToggleButton>
                  <ToggleButton value="list">
                    <FormatListBulletedRounded sx={{ mr: 0.5, fontSize: 16 }} />
                    Lista
                  </ToggleButton>
                  <ToggleButton value="calendar">
                    <CalendarMonthOutlined sx={{ mr: 0.5, fontSize: 16 }} />
                    Calendario
                  </ToggleButton>
                </ToggleButtonGroup>

                <TextField
                  select
                  size="small"
                  label="Ordenar"
                  value={taskSortOrder}
                  onChange={(event) => setTaskSortOrder(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: true }}
                  sx={{
                    minWidth: 210,
                    "& .MuiInputBase-root": {
                      fontSize: 13,
                    },
                  }}
                >
                  <option value="date_desc">Prazo: mais recente</option>
                  <option value="date_asc">Prazo: mais antigo</option>
                  <option value="name_asc">Nome: A-Z</option>
                  <option value="name_desc">Nome: Z-A</option>
                  <option value="responsavel_asc">Responsavel: A-Z</option>
                  <option value="responsavel_desc">Responsavel: Z-A</option>
                </TextField>

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showCompletedTasks}
                      onChange={(event) => setShowCompletedTasks(event.target.checked)}
                    />
                  }
                  label="Mostrar concluidas"
                  sx={{
                    mr: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: 13,
                    },
                  }}
                />

                <Chip
                  label={
                    showCompletedTasks
                      ? `${visibleTasks.length} tarefas`
                      : `${visibleTasks.length} tarefas em aberto`
                  }
                  size="small"
                  variant="outlined"
                />
              </Stack>

              {taskViewMode === "list" ? (
                <Stack spacing={1.5}>
                  {visibleTasks.length === 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px dashed rgba(25, 118, 210, 0.22)",
                        bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.82)" : "#fff"),
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={(theme) => ({
                          color: theme.palette.mode === "dark" ? "rgba(226, 232, 240, 0.78)" : undefined,
                        })}
                      >
                        {currentTasks.length === 0
                          ? "Nenhuma tarefa cadastrada nesta pasta ainda."
                          : "Nao ha tarefas em aberto com o filtro atual."}
                      </Typography>
                    </Paper>
                  ) : (
                    visibleTasks.map((tarefa) => (
                      <Paper
                        key={tarefa.idTarefa}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          border: (theme) =>
                            tarefa.concluida
                              ? `1px solid ${theme.palette.mode === "dark" ? "rgba(102, 187, 106, 0.42)" : "rgba(46, 125, 50, 0.28)"}`
                              : `1px solid ${theme.palette.divider}`,
                          bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.92)" : "#fff"),
                          cursor: "pointer",
                          boxShadow: (theme) =>
                            tarefa.concluida
                              ? theme.palette.mode === "dark"
                                ? "0 10px 20px rgba(22, 101, 52, 0.18)"
                                : "0 8px 16px rgba(22, 163, 74, 0.08)"
                              : "none",
                        }}
                        onClick={() => handleOpenEditTaskDialog(tarefa)}
                      >
                        <Stack spacing={1.25}>
                          <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                              {tarefa.concluida && <TaskAltRounded sx={{ fontSize: 18, color: "success.main" }} />}
                              <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                sx={(theme) => ({
                                  color: theme.palette.mode === "dark" ? "#F8FAFC" : undefined,
                                  textDecoration: tarefa.concluida ? "line-through" : "none",
                                  opacity: tarefa.concluida ? 0.82 : 1,
                                })}
                              >
                                {tarefa.titulo}
                              </Typography>
                              {tarefa.importancia && (
                                <Tooltip title={importanceMap[tarefa.importancia]?.label || tarefa.importancia}>
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: "50%",
                                      bgcolor: importanceMap[tarefa.importancia]?.color || "#94A3B8",
                                      boxShadow: `0 0 0 2px rgba(255,255,255,0.08)`,
                                    }}
                                  />
                                </Tooltip>
                              )}
                              {tarefa.nomeEtapa && <Chip label={tarefa.nomeEtapa} size="small" />}
                              {tarefa.concluida && <Chip label="Concluida" size="small" color="success" variant="outlined" />}
                            </Stack>

                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                              {tarefa.nomeResponsavel && <Chip label={tarefa.nomeResponsavel} size="small" variant="outlined" />}
                              {tarefa.dataPrazo && (
                                !tarefa.concluida && (
                                  <Chip
                                    label={`Prazo ${tarefa.dataPrazo}`}
                                    size="small"
                                    variant="outlined"
                                    color={isTaskOverdue(tarefa, todayDateString) ? "error" : "default"}
                                  />
                                )
                              )}
                              {tarefa.dataConclusao && <Chip label={`Concluida em ${tarefa.dataConclusao}`} size="small" color="success" variant="outlined" />}
                            </Stack>
                          </Stack>

                        </Stack>
                      </Paper>
                    ))
                  )}
                </Stack>
              ) : taskViewMode === "calendar" ? (
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap flexWrap="wrap">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <IconButton
                        size="small"
                        onClick={() =>
                          setCalendarReferenceDate(
                            (current) => new Date(current.getFullYear(), current.getMonth() - 1, 1)
                          )
                        }
                      >
                        <NavigateBeforeRounded />
                      </IconButton>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: 170, textTransform: "capitalize" }}>
                        {calendarMonthLabel}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setCalendarReferenceDate(
                            (current) => new Date(current.getFullYear(), current.getMonth() + 1, 1)
                          )
                        }
                      >
                        <NavigateNextRounded />
                      </IconButton>
                    </Stack>

                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setCalendarReferenceDate(getMonthStart(new Date()))}
                    >
                      Mes atual
                    </Button>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, minmax(120px, 1fr))",
                      gap: 1,
                      overflowX: "auto",
                    }}
                  >
                    {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"].map((label) => (
                      <Typography
                        key={label}
                        variant="caption"
                        sx={{
                          px: 1,
                          py: 0.75,
                          textAlign: "center",
                          fontWeight: 700,
                          color: "text.secondary",
                        }}
                      >
                        {label}
                      </Typography>
                    ))}

                    {calendarDays.map((day) => {
                      const dayKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(
                        day.getDate()
                      ).padStart(2, "0")}`;
                      const dayTasks = calendarTasksByDate[dayKey] || [];
                      const isCurrentMonth = day.getMonth() === calendarReferenceDate.getMonth();
                      const isToday = dayKey === todayDateString;

                      return (
                        <Paper
                          key={dayKey}
                          elevation={0}
                          sx={{
                            minHeight: 132,
                            p: 1,
                            borderRadius: 3,
                            border: isToday ? "1px solid rgba(102, 126, 234, 0.42)" : "1px solid rgba(148, 163, 184, 0.18)",
                            bgcolor: isCurrentMonth ? "background.paper" : "rgba(148, 163, 184, 0.08)",
                            boxShadow: isToday ? "0 8px 18px rgba(102, 126, 234, 0.12)" : "none",
                          }}
                        >
                          <Stack spacing={0.8}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                sx={{
                                  color: isCurrentMonth ? "text.primary" : "text.disabled",
                                }}
                              >
                                {String(day.getDate()).padStart(2, "0")}
                              </Typography>
                              {dayTasks.length > 0 && <Chip label={dayTasks.length} size="small" color="primary" variant="outlined" />}
                            </Stack>

                            <Stack spacing={0.6}>
                              {dayTasks.slice(0, 3).map((tarefa) => (
                                <Box
                                  key={tarefa.idTarefa}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleOpenEditTaskDialog(tarefa)}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                      event.preventDefault();
                                      handleOpenEditTaskDialog(tarefa);
                                    }
                                  }}
                                  sx={() => {
                                    const isOverdueTask = isTaskOverdue(tarefa, todayDateString);

                                    return {
                                      p: 0.75,
                                      borderRadius: 2,
                                      cursor: "pointer",
                                      bgcolor: tarefa.concluida
                                        ? "rgba(76, 175, 80, 0.10)"
                                        : isOverdueTask
                                          ? "rgba(244, 67, 54, 0.08)"
                                          : "rgba(102, 126, 234, 0.08)",
                                      border: `1px solid ${
                                        tarefa.concluida
                                          ? "rgba(76, 175, 80, 0.18)"
                                          : isOverdueTask
                                            ? "rgba(244, 67, 54, 0.42)"
                                            : "rgba(102, 126, 234, 0.14)"
                                      }`,
                                      "&:hover": {
                                        bgcolor: tarefa.concluida
                                          ? "rgba(76, 175, 80, 0.16)"
                                          : isOverdueTask
                                            ? "rgba(244, 67, 54, 0.12)"
                                            : "rgba(102, 126, 234, 0.14)",
                                      },
                                    };
                                  }}
                                >
                                  <Stack spacing={0.45}>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: "50%",
                                          flexShrink: 0,
                                          bgcolor: importanceMap[tarefa.importancia]?.color || "#94A3B8",
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        noWrap
                                        sx={{
                                          color: "text.primary",
                                          textDecoration: tarefa.concluida ? "line-through" : "none",
                                        }}
                                      >
                                        {tarefa.titulo}
                                      </Typography>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {tarefa.nomeEtapa || "Sem etapa"}
                                      {tarefa.nomeResponsavel ? ` - ${tarefa.nomeResponsavel}` : ""}
                                    </Typography>
                                  </Stack>
                                </Box>
                              ))}

                              {dayTasks.length > 3 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{dayTasks.length - 3} tarefa(s)
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Box>
                </Stack>
              ) : (
                <Box
                  ref={boardScrollRef}
                  sx={{
                    display: "grid",
                    gridAutoFlow: "column",
                    gridAutoColumns: { xs: "minmax(260px, 1fr)", md: "minmax(280px, 1fr)" },
                    gap: 2,
                    overflowX: "auto",
                    pb: 1,
                    cursor: "grab",
                  }}
                  onMouseDown={handleBoardMouseDown}
                  onMouseMove={handleBoardMouseMove}
                  onMouseUp={handleBoardMouseUp}
                  onMouseLeave={handleBoardMouseUp}
                >
                  {stagesForBoard.map((etapa) => {
                    const stageTasks = tasksByWorkflow[String(etapa.idWorkflow)] || [];
                    const stageAccent = etapa.conclusivo ? "rgba(102, 187, 106, 0.95)" : "rgba(96, 165, 250, 0.95)";

                    return (
                      <Paper
                        key={etapa.idWorkflow || `${etapa.nomeEtapa}-${etapa.ordem}`}
                        elevation={0}
                        sx={{
                          minHeight: 320,
                          p: 2,
                          borderRadius: 3,
                          bgcolor: "action.hover",
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          outline: dropStageId === etapa.idWorkflow ? "2px solid rgba(25,118,210,0.55)" : "none",
                          outlineOffset: -2,
                        }}
                        onDragOver={(event) => handleStageDragOver(event, etapa)}
                        onDragLeave={() => handleStageDragLeave(etapa)}
                        onDrop={(event) => handleStageDrop(event, etapa)}
                      >
                        <Stack spacing={2}>
                          <Stack spacing={1.25}>
                            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                <Tooltip title={etapa.nomeEtapa}>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={800}
                                    noWrap
                                    sx={{
                                      position: "relative",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      minHeight: 32,
                                      pr: 1,
                                      maxWidth: 160,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      "&::after": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        bottom: -6,
                                        width: 44,
                                        height: 3,
                                        borderRadius: 999,
                                        background: stageAccent,
                                        boxShadow: `0 0 18px ${stageAccent}`,
                                      },
                                    }}
                                  >
                                    {etapa.nomeEtapa}
                                  </Typography>
                                </Tooltip>
                                {canCreateTask && (
                                  <Tooltip title={`Adicionar tarefa em ${etapa.nomeEtapa}`}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenCreateTaskDialog(etapa.idWorkflow)}
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        bgcolor: "#138A5A",
                                        color: "#fff",
                                        borderRadius: 1.5,
                                        "&:hover": {
                                          bgcolor: "#0E6E48",
                                        },
                                      }}
                                    >
                                      <AddRounded sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {!isReadOnlyUser && (
                                  <Tooltip title={`Editar etapa ${etapa.nomeEtapa}`}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenEditStageDialog(etapa)}
                                      sx={{ color: "text.secondary" }}
                                    >
                                      <EditOutlined sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                              {etapa.conclusivo && <Chip label="Conclusivo" size="small" color="success" />}
                            </Stack>

                            <Box
                              sx={{
                                height: 1,
                                borderRadius: 999,
                                background: etapa.conclusivo
                                  ? "linear-gradient(90deg, rgba(102, 187, 106, 0.32) 0%, rgba(102, 187, 106, 0) 100%)"
                                  : "linear-gradient(90deg, rgba(96, 165, 250, 0.24) 0%, rgba(96, 165, 250, 0) 100%)",
                              }}
                            />
                          </Stack>

                          {stageTasks.length > 0 && (
                            <Stack spacing={1.25}>
                              {stageTasks.map((tarefa) => (
                                <Paper
                                  key={tarefa.idTarefa}
                                  elevation={0}
                                  draggable
                                  sx={{
                                    p: 1.75,
                                    borderRadius: 3,
                                    bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.92)" : "#fff"),
                                    border: (theme) =>
                                      tarefa.concluida
                                        ? `1px solid ${theme.palette.mode === "dark" ? "rgba(102, 187, 106, 0.42)" : "rgba(46, 125, 50, 0.28)"}`
                                        : `1px solid ${theme.palette.divider}`,
                                    boxShadow: (theme) =>
                                      tarefa.concluida
                                        ? theme.palette.mode === "dark"
                                          ? "0 10px 20px rgba(22, 101, 52, 0.18)"
                                          : "0 8px 16px rgba(22, 163, 74, 0.08)"
                                        : theme.palette.mode === "dark"
                                          ? "0 10px 20px rgba(2, 6, 23, 0.22)"
                                          : "0 8px 16px rgba(15, 23, 42, 0.06)",
                                    cursor: "pointer",
                                    opacity: draggingTaskId === tarefa.idTarefa ? 0.58 : 1,
                                  }}
                                  onDragStart={(event) => handleTaskDragStart(event, tarefa)}
                                  onDragEnd={handleTaskDragEnd}
                                  onClick={() => handleOpenEditTaskDialog(tarefa)}
                                >
                                  <Stack spacing={1}>
                                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                        {tarefa.concluida && <TaskAltRounded sx={{ fontSize: 16, color: "success.main" }} />}
                                        <Typography
                                          variant="body2"
                                          fontWeight={700}
                                          sx={(theme) => ({
                                            color: theme.palette.mode === "dark" ? "#F8FAFC" : undefined,
                                            textDecoration: tarefa.concluida ? "line-through" : "none",
                                            opacity: tarefa.concluida ? 0.82 : 1,
                                          })}
                                        >
                                          {tarefa.titulo}
                                        </Typography>
                                        {tarefa.importancia && (
                                          <Tooltip title={importanceMap[tarefa.importancia]?.label || tarefa.importancia}>
                                            <Box
                                              sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: "50%",
                                                bgcolor: importanceMap[tarefa.importancia]?.color || "#94A3B8",
                                                flexShrink: 0,
                                              }}
                                            />
                                          </Tooltip>
                                        )}
                                      </Stack>
                                    </Stack>

                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                      {tarefa.concluida && <Chip label="Concluida" size="small" color="success" variant="outlined" />}
                                      {tarefa.nomeResponsavel && <Chip label={tarefa.nomeResponsavel} size="small" />}
                                      {tarefa.dataPrazo && (
                                        !tarefa.concluida && (
                                          <Chip
                                            label={`Prazo ${tarefa.dataPrazo}`}
                                            size="small"
                                            variant="outlined"
                                            color={isTaskOverdue(tarefa, todayDateString) ? "error" : "default"}
                                          />
                                        )
                                      )}
                                      {tarefa.dataConclusao && <Chip label={`Concluida em ${tarefa.dataConclusao}`} size="small" color="success" variant="outlined" />}
                                    </Stack>
                                  </Stack>
                                </Paper>
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}

                  {!isReadOnlyUser && (
                    <Box
                      sx={{
                        minHeight: 320,
                        width: { xs: 56, md: 64 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Tooltip title="Adicionar etapa">
                        <span>
                          <IconButton
                            color="primary"
                            onClick={handleOpenCreateStageDialog}
                            disabled={savingWorkflow}
                            sx={{
                              width: 44,
                              height: 44,
                              border: "1px dashed rgba(25,118,210,0.35)",
                              bgcolor: "rgba(25,118,210,0.05)",
                            }}
                          >
                            <AddRounded />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              )}
            </Stack>
          )}
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: (theme) => `1px solid ${theme.palette.divider}` }}>
          {loading ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : pastas.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Nenhuma pasta encontrada neste contexto.
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: { xs: "none", md: "grid" },
                  gridTemplateColumns: "minmax(280px, 1.7fr) minmax(160px, 1fr) minmax(160px, 0.8fr) 160px",
                  gap: 2,
                  px: 2,
                  pb: 1,
                  color: "text.secondary",
                }}
              >
                <Typography variant="caption" fontWeight={700}>
                  Pasta
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  Workflow
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  Estrutura
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  Acao
                </Typography>
              </Box>

              {pastas.map((pasta) => (
                <Paper
                  key={pasta.idPasta}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border:
                      pasta.ativo === false
                        ? "1px solid rgba(244, 67, 54, 0.28)"
                        : (theme) => `1px solid ${theme.palette.divider}`,
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "minmax(280px, 1.7fr) minmax(160px, 1fr) minmax(160px, 0.8fr) 160px",
                    },
                    gap: 2,
                    alignItems: "center",
                    background:
                      pasta.ativo === false
                        ? "linear-gradient(135deg, rgba(244, 67, 54, 0.10) 0%, rgba(244, 67, 54, 0.04) 100%)"
                        : "transparent",
                    transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 28px rgba(23, 33, 77, 0.08)",
                      borderColor: pasta.ativo === false ? "rgba(244, 67, 54, 0.45)" : "rgba(25,118,210,0.28)",
                    },
                  }}
                  onClick={() => handleOpenFolder(pasta)}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 3,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: pasta.ativo === false ? "rgba(244, 67, 54, 0.14)" : "rgba(25,118,210,0.08)",
                        color: pasta.ativo === false ? "error.main" : "primary.main",
                        flexShrink: 0,
                      }}
                    >
                      <FolderOpenRounded fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography variant="subtitle1" fontWeight={700} noWrap>
                          {pasta.nomePasta}
                        </Typography>
                        {pasta.ativo === false && <Chip label="Inativa" size="small" color="default" />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {pasta.descricao || "Sem descricao cadastrada."}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {pasta.nomeWorkflow || "Workflow sem nome"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fluxo da pasta
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip label={`${pasta.totalSubpastas || 0} subpastas`} size="small" variant="outlined" />
                    <Chip label={`${pasta.totalTarefas || 0} tarefas`} size="small" variant="outlined" />
                  </Stack>

                  <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {pasta.ativo === false && !isReadOnlyUser && (
                        <Tooltip title="Reativar pasta">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleReactivateFolder(pasta);
                              }}
                            >
                              <AutorenewRounded fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      {pasta.tipoPasta !== "INTERNA" && !isReadOnlyUser && (
                        <Tooltip title="Editar pasta">
                          <span>
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenEditFolderDialog(pasta);
                              }}
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      {pasta.tipoPasta !== "INTERNA" && !isReadOnlyUser && (
                        <Tooltip title="Excluir pasta">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenDeleteFolderDialog(pasta);
                              }}
                            >
                              <DeleteOutlineRounded fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      <Button
                        variant="text"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenFolder(pasta);
                        }}
                        startIcon={<TaskAltRounded />}
                      >
                        {Number(pasta.totalSubpastas || 0) > 0 ? "Abrir pasta" : "Selecionar pasta"}
                      </Button>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      <Dialog open={openCreateFolderDialog} onClose={handleCloseFolderDialog} fullWidth maxWidth="sm">
        <DialogTitle>{currentFolder ? "Nova sub-pasta" : "Nova pasta"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {modalError && (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {modalError}
              </Alert>
            )}
            {!canCreateFolder && (
              <Alert severity="warning" sx={{ borderRadius: 3 }}>
                {createRestrictionMessage || "Nao e possivel criar pasta neste contexto."}
              </Alert>
            )}

            <TextField
              label="Nome da pasta"
              name="nomePasta"
              value={folderFormData.nomePasta}
              onChange={handleFolderFormChange}
              fullWidth
              required
            />
            <TextField
              label="Descricao"
              name="descricao"
              value={folderFormData.descricao}
              onChange={handleFolderFormChange}
              multiline
              minRows={3}
              fullWidth
            />
            <TextField
              label="Nome do workflow"
              name="nomeWorkflow"
              value={folderFormData.nomeWorkflow}
              onChange={handleFolderFormChange}
              helperText={currentFolder ? "Ao criar subpasta, o backend copia o workflow da pasta pai." : "Opcional para pastas da raiz."}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFolderDialog} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleCreateFolder} variant="contained" disabled={submitting || !canCreateFolder}>
            {submitting ? "Criando..." : "Criar pasta"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCreateTaskDialog} onClose={handleCloseTaskDialog} fullWidth maxWidth="md">
        <DialogTitle>{taskToEdit ? "Detalhes da tarefa" : "Nova tarefa"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {modalError && (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {modalError}
              </Alert>
            )}
            {!canCreateTask && (
              <Alert severity="warning" sx={{ borderRadius: 3 }}>
                {taskRestrictionMessage || "Nao e possivel criar tarefa nesta pasta."}
              </Alert>
            )}

            <TextField
              label="Titulo"
              name="titulo"
              value={taskFormData.titulo}
              onChange={handleTaskFormChange}
              fullWidth
              required
            />

            <TextField
              label="Descricao"
              name="descricao"
              value={taskFormData.descricao}
              onChange={handleTaskFormChange}
              multiline
              minRows={3}
              fullWidth
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
              }}
            >
              <TextField
                select
                label="Responsavel"
                name="idUsuarioResponsavel"
                value={taskFormData.idUsuarioResponsavel}
                onChange={handleTaskFormChange}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true }}
                fullWidth
              >
                <option value="">Nao definido</option>
                {responsaveis.map((responsavel) => (
                  <option key={responsavel.idUsuario} value={responsavel.idUsuario}>
                    {responsavel.nome}
                  </option>
                ))}
              </TextField>

              <TextField
                select
                label="Etapa"
                name="idWorkflow"
                value={taskFormData.idWorkflow}
                onChange={handleTaskFormChange}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true }}
                fullWidth
                required
              >
                <option value="">Selecione</option>
                {stagesForBoard.map((etapa) => (
                  <option key={etapa.idWorkflow || `${etapa.nomeEtapa}-${etapa.ordem}`} value={etapa.idWorkflow || ""}>
                    {etapa.nomeEtapa}
                  </option>
                ))}
              </TextField>

              <TextField
                select
                label="Importancia"
                name="importancia"
                value={taskFormData.importancia}
                onChange={handleTaskFormChange}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true }}
                fullWidth
              >
                {(bootstrap?.importancias || ["NORMAL", "IMPORTANTE", "URGENTE"]).map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </TextField>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
              }}
            >
              <TextField
                label="Data de inicio"
                name="dataInicio"
                type="date"
                value={taskFormData.dataInicio}
                onChange={handleTaskFormChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Data de prazo"
                name="dataPrazo"
                type="date"
                value={taskFormData.dataPrazo}
                onChange={handleTaskFormChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <TextField
                label="Data de conclusao"
                type="date"
                value={taskToEdit?.dataConclusao || ""}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled
              />
            </Box>

            {taskToEdit && (
              <Stack spacing={1.5} sx={{ pt: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Comentarios
                </Typography>

                {loadingComments ? (
                  <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : taskComments.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      border: "1px dashed rgba(25, 118, 210, 0.22)",
                      bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.82)" : "#fff"),
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Nenhum comentario cadastrado ainda.
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1}>
                    {taskComments.map((comentario) => (
                      <Paper
                        key={comentario.idComentario}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 3,
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.92)" : "#fff"),
                        }}
                      >
                        <Stack spacing={0.75}>
                          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" useFlexGap flexWrap="wrap">
                            <Typography variant="caption" fontWeight={700}>
                              {comentario.nomeAutor || "Usuario"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {comentario.dataHoraCadastro || ""}
                            </Typography>
                          </Stack>
                          <Typography variant="body2">{comentario.comentario}</Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: "1px solid rgba(25, 118, 210, 0.32)",
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "inset 0 0 0 1px rgba(255,255,255,0.03)"
                        : "inset 0 0 0 1px rgba(255,255,255,0.35)",
                    bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(19, 36, 68, 0.72)" : "rgba(25, 118, 210, 0.05)"),
                  }}
                >
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Novo comentario
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Escreva aqui uma atualizacao ou observacao sobre a tarefa.
                      </Typography>
                    </Box>

                    <TextField
                      placeholder="Escreva um novo comentario..."
                      name="comentario"
                      value={commentFormData.comentario}
                      onChange={handleCommentFormChange}
                      multiline
                      minRows={3}
                      fullWidth
                      disabled={submitting || isReadOnlyUser}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          alignItems: "flex-start",
                          bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(10, 18, 38, 0.92)" : "#fff"),
                        },
                      }}
                    />

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="caption" color="text.secondary">
                        {isReadOnlyUser ? "Comentarios estao desabilitados para perfis somente leitura." : "O comentario sera adicionado ao historico da tarefa."}
                      </Typography>
                      <Button
                        onClick={handleCreateComment}
                        variant="contained"
                        disabled={submitting || isReadOnlyUser || !commentFormData.comentario.trim()}
                      >
                        Adicionar comentario
                      </Button>
                    </Box>
                  </Stack>
                </Paper>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTaskDialog} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmitTask} variant="contained" disabled={submitting || !canCreateTask}>
            {submitting ? (taskToEdit ? "Salvando..." : "Criando...") : taskToEdit ? "Salvar tarefa" : "Criar tarefa"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCreateStageDialog} onClose={handleCloseCreateStageDialog} fullWidth maxWidth="xs">
        <DialogTitle>Nova etapa do workflow</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {modalError && (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {modalError}
              </Alert>
            )}
            <TextField
              label="Nome da etapa"
              name="nomeEtapa"
              value={stageFormData.nomeEtapa}
              onChange={handleStageFormChange}
              fullWidth
              required
              autoFocus
              disabled={savingWorkflow}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateStageDialog} disabled={savingWorkflow}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddWorkflowStage}
            variant="contained"
            disabled={savingWorkflow || !stageFormData.nomeEtapa.trim() || isReadOnlyUser}
          >
            {savingWorkflow ? "Cadastrando..." : "Cadastrar etapa"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditStageDialog} onClose={handleCloseEditStageDialog} fullWidth maxWidth="xs">
        <DialogTitle>Editar etapa</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {modalError && (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {modalError}
              </Alert>
            )}
            <TextField
              label="Nome da etapa"
              name="nomeEtapa"
              value={stageEditFormData.nomeEtapa}
              onChange={handleStageEditFormChange}
              fullWidth
              required
              autoFocus
              disabled={savingWorkflow}
            />

            <TextField
              select
              label="Posicao"
              name="ordem"
              value={stageEditFormData.ordem}
              onChange={handleStageEditFormChange}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
              fullWidth
              disabled={savingWorkflow}
            >
              {stagesForBoard.map((etapa, index) => (
                <option key={etapa.idWorkflow || `${etapa.nomeEtapa}-${index}`} value={index + 1}>
                  {index + 1}
                </option>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(stageEditFormData.conclusivo)}
                  onChange={handleStageEditFormChange}
                  name="conclusivo"
                  disabled={savingWorkflow}
                />
              }
              label={stageEditFormData.conclusivo ? "Etapa conclusiva" : "Etapa nao conclusiva"}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
          <Button onClick={handleInactivateStage} color="error" disabled={savingWorkflow}>
            Inativar etapa
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={handleCloseEditStageDialog} disabled={savingWorkflow}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateStage} variant="contained" disabled={savingWorkflow || !stageEditFormData.nomeEtapa.trim()}>
              {savingWorkflow ? "Salvando..." : "Salvar etapa"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditFolderDialog} onClose={handleCloseEditFolderDialog} fullWidth maxWidth="sm">
        <DialogTitle>Editar pasta</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {modalError && (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {modalError}
              </Alert>
            )}
            <TextField
              label="Nome da pasta"
              name="nomePasta"
              value={folderFormData.nomePasta}
              onChange={handleFolderFormChange}
              fullWidth
              required
            />
            <TextField
              label="Descricao"
              name="descricao"
              value={folderFormData.descricao}
              onChange={handleFolderFormChange}
              multiline
              minRows={3}
              fullWidth
            />
            <TextField
              label="Nome do workflow"
              name="nomeWorkflow"
              value={folderFormData.nomeWorkflow}
              onChange={handleFolderFormChange}
              fullWidth
            />
            <FormControlLabel
              control={<Switch checked={Boolean(folderFormData.ativo)} onChange={handleFolderActiveChange} />}
              label={folderFormData.ativo ? "Pasta ativa" : "Pasta inativa"}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditFolderDialog} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleUpdateFolder} variant="contained" disabled={submitting}>
            {submitting ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteFolderDialog} onClose={handleCloseDeleteFolderDialog} fullWidth maxWidth="xs">
        <DialogTitle>Excluir pasta</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {modalError && (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {modalError}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              Tem certeza que deseja excluir a pasta <strong>{folderToDelete?.nomePasta}</strong>?
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteFolderDialog} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteFolder} color="error" variant="contained" disabled={submitting}>
            {submitting ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default TaskAgentPastas;
